import {
  collection,
  query,
  where,
  addDoc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  orderBy,
  writeBatch,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { GETCOUNT } from "../Redux/actions/ChatActions";

/**
 * Creates a chat room if one does not already exist between the specified patient and doctor.
 * @param {string} patientID - The ID of the patient.
 * @param {string} doctorID - The ID of the doctor.
 * @returns {Promise<string>} - The chat room ID.
 */
export async function createChatRoom(patientID, doctorID) {
  const chatsRef = collection(db, "chats");

  // Check if a chat already exists between these users
  const q = query(chatsRef, where("users", "array-contains", patientID));
  const chatSnapshot = await getDocs(q);

  const existingChat = chatSnapshot.docs.find((doc) =>
    doc.data().users.includes(doctorID)
  );

  if (existingChat) {
    return existingChat.id; // Return existing chat room ID
  }

  // Create a new chat room
  const newChat = await addDoc(chatsRef, {
    users: [patientID, doctorID],
    createdAt: new Date(),
  });

  return newChat.id; // Return new chat room ID
}

export function getChatList(userID, setChatList) {
  const chatsRef = collection(db, "messages");
  const q = query(chatsRef, where("users", "array-contains", userID));

  onSnapshot(q, (snapshot) => {
    const chats = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setChatList(chats);
  });
}

export async function sendMessage(senderID, recipientID, text) {
  const messagesRef = collection(db, "messages");
  await addDoc(messagesRef, {
    senderID,
    recipientID,
    text,
    timestamp: serverTimestamp(),
    seen: false,
  });
}

// export function listenForDoctorsList(patientID, setDoctorsList) {
//   if (!patientID) return;

//   const messagesRef = collection(db, "messages");

//   // Query messages where the patient is the sender
//   const q = query(
//     messagesRef,
//     where("senderID", "==", patientID),
//   );

//   const unsubscribe = onSnapshot(q, (snapshot) => {
//     // Extract unique doctor IDs from messages
//     const doctorIdsSet = new Set(
//       snapshot.docs.map(doc => doc.data().recipientID) // Doctors are recipients
//     );

//     // Convert Set to an array
//     const doctorIds = Array.from(doctorIdsSet);

//     // Set doctor list
//     setDoctorsList(doctorIds);
//   });

//   // Return cleanup function
//   return () => {
//     unsubscribe();
//   };
// }

export function listenForDoctorsList(patientID, setDoctorsList) {
  if (!patientID) return;

  const messagesRef = collection(db, "messages");

  // Query messages where the patient is either the sender or recipient
  const q = query(
    messagesRef,
    where("senderID", "==", patientID) // Messages sent by the patient
  );

  const q2 = query(
    messagesRef,
    where("recipientID", "==", patientID) // Messages received by the patient
  );

  // Listen to both queries
  const unsubscribe1 = onSnapshot(q, (snapshot) =>
    updateDoctorList(snapshot, setDoctorsList)
  );
  const unsubscribe2 = onSnapshot(q2, (snapshot) =>
    updateDoctorList(snapshot, setDoctorsList)
  );

  function updateDoctorList(snapshot, setDoctorsList) {
    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const doctorID =
        data.senderID === patientID ? data.recipientID : data.senderID;
      setDoctorsList((prev) => [...new Set([...prev, doctorID])]); // Add unique doctor IDs
    });
  }

  // Cleanup function to unsubscribe both listeners
  return () => {
    unsubscribe1();
    unsubscribe2();
  };
}

export function listenForMessages(user1ID, user2ID, setMessages) {
  const messagesRef = collection(db, "messages");

  // Firestore doesn't support querying both senderID and recipientID using `in` together.
  // We need to perform separate checks for both sender and recipient
  const q = query(
    messagesRef,
    where("senderID", "==", user1ID),
    where("recipientID", "==", user2ID),
    orderBy("timestamp")
  );

  const q2 = query(
    messagesRef,
    where("senderID", "==", user2ID),
    where("recipientID", "==", user1ID),
    orderBy("timestamp")
  );

  // Combine both queries with `onSnapshot` listeners
  const unsubscribe1 = onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setMessages((prevMessages) => {
      const existingMessageIds = prevMessages.map((msg) => msg.id);
      const filteredMessages = messages.filter(
        (msg) => !existingMessageIds.includes(msg.id)
      );
      return [...prevMessages, ...filteredMessages]; // Add new messages to the bottom
    });
  });

  const unsubscribe2 = onSnapshot(q2, (snapshot) => {
    const messages = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setMessages((prevMessages) => {
      const existingMessageIds = prevMessages.map((msg) => msg.id);
      const filteredMessages = messages.filter(
        (msg) => !existingMessageIds.includes(msg.id)
      );
      return [...prevMessages, ...filteredMessages]; // Add new messages to the bottom
    });
  });

  // Return cleanup function
  return () => {
    unsubscribe1();
    unsubscribe2();
  };
}

export async function markMessagesAsSeen(recipientID, senderID) {
  const messagesRef = collection(db, "messages");

  // Query for messages where the recipient is the current user and `seen` is false
  const q = query(
    messagesRef,
    where("recipientID", "==", recipientID),
    where("senderID", "==", senderID),
    where("seen", "==", false)
  );

  const querySnapshot = await getDocs(q);
  const batch = writeBatch(db);

  // Add all messages to the batch
  querySnapshot.forEach((doc) => {
    batch.update(doc.ref, { seen: true });
  });

  // Commit the batch
  await batch.commit();
}

export async function countUnreadMessages(userID, onCountChange) {
  const messagesRef = collection(db, "messages");

  // Query for messages where the recipient is the user and `seen` is false
  const q = query(
    messagesRef,
    where("recipientID", "==", userID),
    where("seen", "==", false)
  );

  // const querySnapshot = await getDocs(q);

  // Return the count of unread messages
  // return querySnapshot.size;
  return onSnapshot(q, (snapshot) => {
    onCountChange(snapshot.size);
  });
}

export async function countUnreadMessagesByPatient(userID) {
  const messagesRef = collection(db, "messages");

  // Query for unread messages where the user is the recipient
  const q = query(
    messagesRef,
    where("recipientID", "==", userID),
    where("seen", "==", false)
  );

  const querySnapshot = await getDocs(q);

  // Create a map to store unread message counts per doctor
  const unreadCounts = {};

  querySnapshot.forEach((doc) => {
    const senderID = doc.data().senderID;
    unreadCounts[senderID] = (unreadCounts[senderID] || 0) + 1;
  });

  return unreadCounts; // Returns an object: { doctorID1: count, doctorID2: count, ... }
}

export function listenUnreadMessagesByPatient(userID, onChange) {
  const messagesRef = collection(db, "messages");

  const q = query(
    messagesRef,
    where("recipientID", "==", userID),
    where("seen", "==", false)
  );

  return onSnapshot(q, (snapshot) => {
    const unreadCounts = {};

    snapshot.forEach((doc) => {
      const senderID = doc.data().senderID;
      unreadCounts[senderID] = (unreadCounts[senderID] || 0) + 1;
    });

    onChange(unreadCounts);
  });
}
